import { Link } from "react-router";

import { frameHook } from "@/components/ui/box.schema";
import { cn } from "@/lib/utils";
import { courseHeaderDestinations, courseWorkspaceSectionHref, courseWorkspaceSections, type CourseWorkspaceView } from "@/navigation/course-workspace";

const TAB = "inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center border-b-2 text-ui transition-colors duration-[var(--motion-fast)] focus-visible:rounded-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring";

/** The course's three primary destinations (#1393 contract 5): Desk,
 * Coursework, Gradebook or Grades. Switching keeps the selected session as
 * context and leaves each view's record selectors with its owner. The tabs are
 * the frame (CASE-006): choosing one is page navigation. People, Manage course
 * and All notices are addressed pages, not tabs, so none is current there. */
export function CourseViewNavigation({ courseHref, selectedSessionId, teaching, activeView }: {
  courseHref: string;
  selectedSessionId: string | null;
  teaching: boolean;
  /** None on a session's page: it is Desk's record, not a tab. */
  activeView?: CourseWorkspaceView;
}) {
  return (
    <nav aria-label="Course views" {...frameHook("tabs")} className="flex min-w-0 flex-wrap gap-x-5 border-b border-border bg-background lg:sticky lg:top-0 lg:z-20 lg:gap-x-6">
      {courseWorkspaceSections(teaching).map(({ view, label }) => (
        <Link
          key={view}
          to={courseWorkspaceSectionHref(courseHref, view, selectedSessionId)}
          aria-current={view === activeView ? "page" : undefined}
          className={cn(TAB, view === activeView ? "border-foreground font-semibold text-foreground" : "border-transparent text-muted-foreground hover:text-foreground")}
        >
          {label}
        </Link>
      ))}
      {/* Desk's stable in-page entry to The course, for pointer and keyboard alike (#1393 contract 5). It moves focus and pushes no history entry. */}
      {activeView === "desk" && (
        <a
          href="#the-course"
          data-the-course-link=""
          onClick={(event) => {
            const target = document.getElementById("the-course");
            if (!target) return;
            event.preventDefault();
            target.focus({ preventScroll: true });
            target.scrollIntoView({ block: "start" });
          }}
          className="ms-auto inline-flex min-h-11 shrink-0 items-center whitespace-nowrap border-b-2 border-transparent text-ui text-muted-foreground hover:text-foreground focus-visible:rounded-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
        >
          The course
        </a>
      )}
    </nav>
  );
}

/**
 * The band's header destinations (#1393 contract 4): People, and Manage course
 * for a teaching perspective. They are links by existing capability and grant
 * nothing; each page keeps its own refusal. The open one is marked current.
 */
export function CourseHeaderDestinations({ courseHref, teaching, activeView }: {
  courseHref: string;
  teaching: boolean;
  activeView?: CourseWorkspaceView;
}) {
  return (
    <nav aria-label="Course" data-course-header="" className="flex flex-wrap items-center gap-x-5 gap-y-1">
      {courseHeaderDestinations(teaching).map(({ view, label }) => (
        <Link
          key={view}
          to={courseWorkspaceSectionHref(courseHref, view)}
          aria-current={view === activeView ? "page" : undefined}
          className="inline-flex min-h-11 min-w-11 items-center justify-center text-ui text-primary underline-offset-4 hover:underline aria-[current=page]:font-semibold aria-[current=page]:text-foreground aria-[current=page]:no-underline focus-visible:rounded-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {label}
        </Link>
      ))}
    </nav>
  );
}
